import { Platform } from 'react-native';
import type { DeliveryAddress, DeliveryCheckoutIntents, DeliveryQuoteResult } from '../types/delivery';
import { warmupDeliveryConfig } from './deliveryConfigService';

declare const process: { env: Record<string, string | undefined> };

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_PAYMENT_API_URL ||
  'https://bookapt-payment-api.onrender.com';

export async function quoteDeliveryCheckout(input: {
  businessId: string;
  subtotalCents: number;
  deliveryAddress: DeliveryAddress;
  dropoffPhone?: string;
  dropoffName?: string;
  tipCents?: number;
}): Promise<DeliveryQuoteResult> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/checkout/quote-delivery`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        businessId: input.businessId,
        subtotalCents: input.subtotalCents,
        deliveryAddress: input.deliveryAddress,
        dropoffPhone: input.dropoffPhone || input.deliveryAddress.phone,
        dropoffName: input.dropoffName,
        tipCents: input.tipCents ?? 0,
      }),
    });
    const data = await res.json();
    if (!res.ok || !data?.success) {
      return {
        success: false,
        deliveryFeeCents: 0,
        serviceFeeCents: 0,
        deliveryAndFeesCents: 0,
        tipCents: 0,
        subtotalCents: input.subtotalCents,
        totalCents: 0,
        etaMinutes: 0,
        error: data?.error || 'Could not get delivery quote',
      };
    }
    return { success: true, ...data };
  } catch (e: any) {
    return {
      success: false,
      deliveryFeeCents: 0,
      serviceFeeCents: 0,
      deliveryAndFeesCents: 0,
      tipCents: 0,
      subtotalCents: input.subtotalCents,
      totalCents: 0,
      etaMinutes: 0,
      error: e?.message || 'Network error',
    };
  }
}

export async function createDeliveryCheckoutIntents(input: {
  businessId: string;
  subtotalCents: number;
  deliveryFeeCents: number;
  tipCents: number;
  deliveryAddress: DeliveryAddress;
  customerId?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone: string;
  quoteExternalDeliveryId?: string;
  etaMinutes?: number;
  lineItems?: Array<{ name: string; quantity: number; priceCents: number }>;
}): Promise<DeliveryCheckoutIntents> {
  try {
    const res = await fetch(`${API_BASE_URL}/api/checkout/create-delivery-intents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const data = await res.json();
    if (!res.ok || !data?.success) {
      return {
        success: false,
        orderId: '',
        subtotalCents: 0,
        deliveryFeeCents: 0,
        serviceFeeCents: 0,
        deliveryAndFeesCents: 0,
        tipCents: 0,
        totalCents: 0,
        foodClientSecret: '',
        foodPaymentIntentId: '',
        logisticsClientSecret: '',
        logisticsPaymentIntentId: '',
        error: data?.error || 'Could not start delivery checkout',
      };
    }
    return { success: true, ...data };
  } catch (e: any) {
    return {
      success: false,
      orderId: '',
      subtotalCents: 0,
      deliveryFeeCents: 0,
      serviceFeeCents: 0,
      deliveryAndFeesCents: 0,
      tipCents: 0,
      totalCents: 0,
      foodClientSecret: '',
      foodPaymentIntentId: '',
      logisticsClientSecret: '',
      logisticsPaymentIntentId: '',
      error: e?.message || 'Network error',
    };
  }
}

/** Check merchant Stripe Connect is ready (required for delivery). */
export async function checkStripeConnectReady(
  businessId: string,
  stripeConnectAccountId?: string
): Promise<{ ready: boolean; accountId?: string }> {
  try {
    const q =
      stripeConnectAccountId && stripeConnectAccountId.startsWith('acct_')
        ? `?accountId=${encodeURIComponent(stripeConnectAccountId)}`
        : '';
    const res = await fetch(`${API_BASE_URL}/api/stripe-connect/status/${businessId}${q}`);
    const data = await res.json();
    const ready = !!(data.connected && (data.chargesEnabled || data.ready));
    return { ready, accountId: data.accountId };
  } catch {
    return { ready: false };
  }
}

/** Wake Render dyno (web) and prefetch delivery config including service fee from env. */
export function warmupDeliveryApi(): void {
  warmupDeliveryConfig();
  if (Platform.OS === 'web' && typeof fetch !== 'undefined') {
    fetch(`${API_BASE_URL}/api/health`, { method: 'GET', cache: 'no-store' }).catch(() => {});
  }
}
